import type { AppNotification } from '../components/Preview/api';

export type ProfileAccessParams = {
  accessRequestId: string | null;
  recruiterEmail: string | null;
  requestedField: string | null;
};

function messageText(notification: AppNotification): string {
  return String(notification.message || '').toLowerCase();
}

export function extractEmailFromText(text: string): string | null {
  const match = text.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
  return match?.[0]?.toLowerCase() || null;
}

export function parseProfileAccessParams(notification: AppNotification): ProfileAccessParams {
  const rawUrl = String(notification.action_url || '');
  let accessRequestId: string | null = null;
  let recruiterEmail: string | null = null;
  let requestedField: string | null = null;

  if (rawUrl.includes('?')) {
    const query = rawUrl.slice(rawUrl.indexOf('?') + 1);
    const params = new URLSearchParams(query);
    accessRequestId = params.get('accessRequest');
    recruiterEmail = params.get('recruiterEmail');
    requestedField = params.get('requestedField');
  }

  if (!recruiterEmail) {
    recruiterEmail = extractEmailFromText(notification.message || '');
  }

  return { accessRequestId, recruiterEmail, requestedField };
}

export function isProfileAccessNotification(notification: AppNotification): boolean {
  const type = String(notification.type || '').toLowerCase();
  const msg = messageText(notification);
  return (
    type === 'profileaccessrequest' ||
    /profile access request|requested profile access|requesting access to your/i.test(msg)
  );
}

export function isChatRequestNotification(notification: AppNotification): boolean {
  const type = String(notification.type || '').toLowerCase();
  const msg = messageText(notification);
  return (
    type === 'chatrequest' ||
    (msg.includes('chat request') && !msg.includes('accepted') && !msg.includes('declined'))
  );
}

export function isQuestionNotification(notification: AppNotification): boolean {
  const type = String(notification.type || '').toLowerCase();
  const msg = messageText(notification);
  return (
    type === 'newquestion' ||
    (msg.includes('question') && !msg.includes('answered') && !msg.includes('profile access'))
  );
}

export function isMeetingNotification(notification: AppNotification): boolean {
  const msg = messageText(notification);
  return msg.includes('meeting') || msg.includes('calendar');
}

export function canRespondToProfileAccessNotification(notification: AppNotification): boolean {
  if (!isProfileAccessNotification(notification)) return false;
  return Boolean(parseProfileAccessParams(notification).recruiterEmail);
}

/** Map legacy backend paths to skipscreen-app routes. */
export function rewriteNotificationPath(path: string): string {
  const trimmed = path.trim();
  if (!trimmed) return '/dashboard';

  const [pathname, query = ''] = trimmed.split('?');
  const params = new URLSearchParams(query);

  if (pathname === '/chat-requests' || pathname === '/messages/requests') {
    const requestId = params.get('request_id') || params.get('request') || params.get('requestId');
    return requestId
      ? `/messages/requests?request=${encodeURIComponent(requestId)}`
      : '/messages/requests';
  }

  if (pathname === '/message-hub/questions' || pathname === '/messages/questions') {
    const requestId = params.get('request_id') || params.get('question') || params.get('requestId');
    return requestId
      ? `/messages/questions?request=${encodeURIComponent(requestId)}`
      : '/messages/questions';
  }

  if (pathname === '/message-hub') {
    return '/messages/questions';
  }

  if (pathname.startsWith('/dashboard')) {
    return trimmed;
  }

  if (pathname === '/calendar' || pathname.startsWith('/calendar')) {
    return '/calendar';
  }

  if (pathname === '/activity' || pathname.startsWith('/activity')) {
    return trimmed;
  }

  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
}

export function resolveNotificationNavigationTarget(notification: AppNotification): string {
  if (notification.action_url?.trim()) {
    return rewriteNotificationPath(notification.action_url);
  }

  const msg = messageText(notification);

  if (isProfileAccessNotification(notification)) {
    const { accessRequestId, recruiterEmail, requestedField } =
      parseProfileAccessParams(notification);
    const params = new URLSearchParams();
    if (accessRequestId) params.set('accessRequest', accessRequestId);
    if (recruiterEmail) params.set('recruiterEmail', recruiterEmail);
    if (requestedField) params.set('requestedField', requestedField);
    const qs = params.toString();
    return qs ? `/dashboard?${qs}` : '/dashboard';
  }

  if (isQuestionNotification(notification)) {
    return '/messages/questions';
  }

  if (isChatRequestNotification(notification)) {
    return '/messages/requests';
  }

  if (isMeetingNotification(notification)) {
    return '/calendar';
  }

  if (msg.includes('liked your prompt')) {
    return '/prompts';
  }

  if (msg.includes('reference')) {
    return '/dashboard/references';
  }

  if (msg.includes('message') || msg.includes('asked') || msg.includes('new message')) {
    return '/messages';
  }

  return '/activity';
}
